// Staff-only repair workspace for reviews whose upload never persisted a
// `review_files` row (timeline says a file was uploaded, but the OCR
// pipeline finds none). Root cause of the "stuck at Reading Invoice"
// incident: duplicate-hash short circuit, RLS insert error, or any other
// failure path in fileService.upload after the storage upload succeeded.
//
// This page lets staff:
//   - see the list of orphan reviews
//   - re-upload a file (goes through the same fileService.upload path)
//   - resume AI processing once at least one review_files row exists

import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, RefreshCw, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import {
  AdminShell,
  AdminPageHeader,
  AdminCard,
  AdminButton,
  AdminBadge,
  AdminEmptyState,
} from "@/components/admin";
import { fileService, DuplicateUploadError, InvalidUploadError } from "@/lib/services/fileService";
import {
  listReviewsMissingFiles,
  countReviewFiles,
  resumeAIProcessing,
} from "@/lib/ai.functions";

export const Route = createFileRoute("/operations/repair-uploads")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Repair Uploads — Operations" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["staff", "admin"]}>
      <RepairUploadsPage />
    </AuthGate>
  ),
});

interface OrphanReview {
  id: string;
  file_number: string | null;
  status: string | null;
  customer_id: string;
  created_at: string;
  submission_method: string | null;
}

function RepairUploadsPage() {
  const { user } = useAuth();
  const [orphans, setOrphans] = useState<OrphanReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listReviewsMissingFiles();
      setOrphans((res.orphans ?? []) as OrphanReview[]);
    } catch (e) {
      toast.error(`Failed to load orphans: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <AdminShell>
      <AdminPageHeader
        kicker="Staff · Repair"
        title="Reviews missing uploaded files"
        description="These reviews recorded a file_uploaded timeline event but have zero rows in review_files. OCR cannot proceed until a file is re-attached. Uploading here uses the same validated, hashed, RLS-scoped path as the customer submit flow and resumes the AI pipeline on success."
        breadcrumbs={[
          { label: "Operations", to: "/operations" },
          { label: "Repair uploads" },
        ]}
        actions={
          <AdminButton
            variant="secondary"
            size="sm"
            onClick={() => refresh()}
            loading={loading}
            icon={<RefreshCw className="h-4 w-4" />}
          >
            Refresh
          </AdminButton>
        }
      />

      <div className="space-y-3">
        {loading && (
          <AdminCard>
            <div className="text-sm text-muted-foreground">Loading…</div>
          </AdminCard>
        )}
        {!loading && orphans.length === 0 && (
          <AdminEmptyState
            title="No orphan reviews"
            description="Every review with a file_uploaded event has at least one review_files row."
          />
        )}
        {orphans.map((r) => (
          <OrphanRow
            key={r.id}
            review={r}
            currentUserId={user?.id ?? ""}
            busy={busyId === r.id}
            onBusy={setBusyId}
            onRepaired={() => refresh()}
          />
        ))}
      </div>
    </AdminShell>
  );
}

function OrphanRow({
  review,
  currentUserId,
  busy,
  onBusy,
  onRepaired,
}: {
  review: OrphanReview;
  currentUserId: string;
  busy: boolean;
  onBusy: (id: string | null) => void;
  onRepaired: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    onBusy(review.id);
    try {
      // Re-upload as the OWNING customer's user id so RLS on both storage
      // and review_files matches (the path convention is `${userId}/...`
      // and the RLS uses the first folder). Fall back to current staff id
      // if for some reason we're missing it — the insert will still work
      // via admin path from the resume server fn, but storage will refuse.
      const uploadUserId = review.customer_id || currentUserId;
      const bucket =
        review.submission_method === "photo" ? "photos" : "invoices";
      try {
        await fileService.upload({
          bucket,
          userId: uploadUserId,
          reviewId: review.id,
          file,
          kind: review.submission_method === "photo" ? "photo" : "invoice",
          allowDuplicate: true,
        });
      } catch (e) {
        if (e instanceof DuplicateUploadError) {
          // Force through with a re-hashed variant: staff already opted in
          // via allowDuplicate above, so this shouldn't happen — surface it.
          throw new Error(`Duplicate detected: ${e.duplicate.existingFileName}`);
        }
        if (e instanceof InvalidUploadError) {
          throw e;
        }
        throw e;
      }
      // Verify persistence via the server-side count.
      const { count } = await countReviewFiles({ data: { review_id: review.id } });
      if (!count || count === 0) {
        throw new Error("review_files row not visible after upload");
      }
      toast.success(`File attached (${count} row${count === 1 ? "" : "s"}). Resuming pipeline…`);
      // Resume AI processing from the failed OCR stage.
      const res = await resumeAIProcessing({ data: { review_id: review.id } });
      toast.success(`Pipeline resumed — reset ${res.reset} job(s).`);
      onRepaired();
    } catch (e) {
      toast.error(`Repair failed: ${(e as Error).message}`);
    } finally {
      onBusy(null);
    }
  }

  return (
    <div className="rounded-2xl border border-warning/40 bg-card p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <div className="font-mono text-sm text-navy">{review.file_number ?? review.id}</div>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
              {review.status ?? "unknown"}
            </span>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
              via {review.submission_method ?? "n/a"}
            </span>
          </div>
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">
            review_id={review.id} · customer={review.customer_id} · created=
            {new Date(review.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-sm font-semibold text-navy-foreground hover:opacity-90 disabled:opacity-50"
          >
            <UploadIcon className="h-4 w-4" />
            {busy ? "Uploading…" : "Re-upload file"}
          </button>
        </div>
      </div>
    </div>
  );
}
